import { useLanguage } from '../context/LanguageContext';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Heart, Target, Users } from 'lucide-react';
import useSEOPage from '../hooks/useSEOPage';

export default function APropos() {
  const { t } = useLanguage();
  const { seoData } = useSEOPage('a-propos');

  return (
    <>
      {seoData && (
        <Helmet>
          <title>{seoData.meta_title || seoData.title || 'À propos de Joey Cyan | Notre histoire'}</title>
          <meta name="description" content={seoData.meta_description || seoData.description || 'Découvrez l\'histoire de Joey Cyan, notre mission et notre vision pour révolutionner le monde des lunettes connectées.'} />
          {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
          {seoData.og_image && <meta property="og:image" content={seoData.og_image} />}
        </Helmet>
      )}
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-16">
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">{t('nav.about')}</p>
            <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('about.title')}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Joey Cyan est née d'une vision : rendre la technologie augmentée accessible, élégante et utile au quotidien.
            </p>
          </div>

          {/* Mission, Vision, Valeurs */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Target size={32} className="text-[#00aaff]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Notre Mission</h2>
              <p className="text-gray-600">
                Démocratiser la réalité augmentée en créant des lunettes connectées accessibles, 
                performantes et designs qui améliorent votre quotidien.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Heart size={32} className="text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Nos Valeurs</h2>
              <p className="text-gray-600">
                Innovation, qualité, respect de la vie privée et engagement environnemental. 
                Nos lunettes sont conçues pour durer et être réparables.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Users size={32} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Notre Vision</h2>
              <p className="text-gray-600">
                Un monde où la technologie s'intègre naturellement à notre vision, 
                pour nous connecter au monde sans nous déconnecter de la réalité.
              </p>
            </div>
          </div>

          {/* Story */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">L'histoire de Joey Cyan</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                Joey Cyan est née d'une vision simple : proposer des lunettes au design affirmé, accessibles et inspirées par une culture globale.
              </p>
              <p className="mb-4">
                L'aventure commence en 2018, en Chine, où la marque prend ses racines au cœur d'un écosystème dynamique mêlant innovation, savoir-faire technique et production de qualité. C'est là que les premières collections voient le jour, avec une attention particulière portée aux détails, aux matériaux et aux tendances internationales.
              </p>
              <p className="mb-4">
                Au fil des années, Joey Cyan évolue et s'ouvre au monde. La marque traverse ensuite les États-Unis, véritable carrefour de styles et d'influences, où elle affine son identité et développe une approche plus audacieuse, entre streetwear et élégance contemporaine.
              </p>
              <p className="mb-4">
                En 2025, Joey Cyan arrive en France avec l'ambition de s'imposer comme une marque de lunettes moderne et distinctive. Inspirée par son parcours international, elle propose aujourd'hui des modèles qui allient style, qualité et accessibilité.
              </p>
              <p>
                Joey Cyan, c'est plus qu'une marque : c'est un voyage entre les cultures, une rencontre entre design et caractère.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-[#00aaff] to-blue-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">Rejoignez l'aventure</h2>
            <p className="text-xl mb-6 text-blue-50">Découvrez nos lunettes connectées nouvelle génération</p>
            <a 
              href="/collection" 
              className="inline-block bg-white text-[#00aaff] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-xl"
            >
              Voir la collection →
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
